// /app/frontend/src/pages/seo/StateOfBlackRelationships2026Page.jsx
// Authority report — observations & analysis (no fabricated stats).
import React from 'react';
import PublicSEOHeader from '../../components/PublicSEOHeader';
import {
  SEOHead,
  organizationSchema,
  buildArticleSchema,
  buildBreadcrumbSchema,
  ReportHero,
  ReportSection,
  AboutThisReport,
  PullQuote,
  BottomCTA,
} from './SEOUtils';

const URL_PATH = '/state-of-black-relationships-2026';
const PUBLISHED_ISO = '2026-02-15';
const PUBLISHED_HUMAN = 'February 2026';

export default function StateOfBlackRelationships2026Page() {
  const schemas = [
    organizationSchema,
    buildArticleSchema({
      headline: 'The State of Black Relationships Report 2026',
      description:
        'An in-depth look at modern Black relationships, dating trends, family-building, intentional partnerships, community, and connection across the African diaspora.',
      urlPath: URL_PATH,
      datePublishedISO: PUBLISHED_ISO,
    }),
    buildBreadcrumbSchema([
      { name: 'Home', url: '/' },
      { name: 'Reports', url: '/reports' },
      { name: 'State of Black Relationships 2026', url: URL_PATH },
    ]),
  ];

  return (
    <div className="min-h-screen bg-white">
      <PublicSEOHeader />
      <SEOHead
        title="State of Black Relationships Report 2026 | UnityLove"
        description="An in-depth look at modern Black relationships, dating trends, family-building, intentional partnerships, community, and connection across the African diaspora."
        canonical={URL_PATH}
        schemas={schemas}
      />

      <ReportHero
        eyebrow="UnityLove Research"
        dateline={`Published ${PUBLISHED_HUMAN}`}
        headline="The State of Black Relationships Report 2026"
        dek="An honest analysis of how Black love is being chosen, built, and protected — across the diaspora, the country, and the platforms that mediate connection."
      />

      <article className="max-w-3xl mx-auto px-4 py-12">
        <ReportSection n={1} title="Executive Summary">
          <p>
            Black relationships in 2026 are entering a new chapter. After a decade in which dating apps dominated the experience of finding a partner, a generation is now asking different questions: not just who do I meet, but who do I want to build with? Marriage, family-building, and long-term partnership are returning to the cultural center, even as the structures around them are diversifying.
          </p>
          <p>
            This report synthesizes observations from the UnityLove community, public research on Black relationships and family life, and conversations with members across the African diaspora. Five themes consistently emerge.
          </p>
        </ReportSection>

        <ReportSection n={2} title="Key Findings">
          <ul className="list-disc list-inside ml-2 space-y-2">
            <li><strong className="text-slate-900">Intentional dating is rising.</strong> Casual hookup culture is losing cultural cachet; long-term-minded dating is returning to the fore — especially among Black singles in their late twenties through forties.</li>
            <li><strong className="text-slate-900">Family-building remains a priority.</strong> Across diverse relationship structures (monogamous, polyamorous, plural), family — including marriage, children, and intergenerational connection — is being discussed earlier and more openly than in prior decades.</li>
            <li><strong className="text-slate-900">Verification and trust matter more than ever.</strong> The rise of catfishing, AI-generated profiles, and fetishization on mainstream apps has made authenticity the single most valued attribute in modern Black dating.</li>
            <li><strong className="text-slate-900">Community is increasingly important.</strong> Dating is no longer a private transaction between two strangers; it is increasingly embedded in community contexts — HBCU networks, faith communities, professional circles, cultural spaces.</li>
            <li><strong className="text-slate-900">Relationship structures are diversifying.</strong> Monogamy is no longer the default assumption. Polyamory, plural family-building, and intentional non-traditional structures are gaining cultural visibility across the diaspora.</li>
          </ul>
        </ReportSection>

        <ReportSection n={3} title="What Singles Want Most">
          <p>
            Across the conversations and profile data we observe at UnityLove, the priorities Black singles consistently name have shifted from a generation ago. Five qualities come up repeatedly, often in the same order:
          </p>
          <ol className="list-decimal list-inside ml-2 space-y-1.5">
            <li><strong className="text-slate-900">Shared values</strong> — faith, family vision, cultural alignment</li>
            <li><strong className="text-slate-900">Communication</strong> — the ability to talk through hard things without retreating</li>
            <li><strong className="text-slate-900">Emotional maturity</strong> — therapy, self-awareness, accountability</li>
            <li><strong className="text-slate-900">Stability</strong> — financial, emotional, life-rhythm</li>
            <li><strong className="text-slate-900">Long-term compatibility</strong> — alignment on the destination, not just the chemistry</li>
          </ol>
          <PullQuote>
            People keep saying they want chemistry. What they actually want is alignment.
          </PullQuote>
        </ReportSection>

        <ReportSection n={4} title="The Return of Family Building">
          <p>
            For much of the 2010s, mainstream dating culture framed marriage and children as optional or even outdated. That framing is reversing — particularly in Black communities, where intergenerational family, marriage, and legacy have always been culturally central regardless of what the broader discourse said.
          </p>
          <p>
            Members across UnityLove are discussing marriage timelines, parenting philosophies, generational wealth, and household structure on first conversations — not as red flags, but as table stakes. Family-building is being treated as a project requiring intentionality, not a default that happens after dating.
          </p>
          <p>
            This shift cuts across all relationship structures. Monogamous couples plan family. Polyamorous and plural families plan family. The difference is no longer whether to build a family; it is how to build one that lasts.
          </p>
        </ReportSection>

        <ReportSection n={5} title="The Trust Challenge">
          <p>
            The single biggest friction in modern Black dating — across every platform and every demographic — is trust. Catfishing, stolen photos, AI-generated faces, scam profiles, and racialized fetishization have left a generation skeptical of who is on the other end of any conversation. Verification is no longer a nice-to-have. It is the foundation everything else gets built on.
          </p>
          <p>
            We are seeing this shift mirrored across the industry: identity verification is moving from a premium feature to a baseline expectation. Members who go through identity verification get noticed faster and treated with more care. The verified badge has become the new social proof.
          </p>
        </ReportSection>

        <ReportSection n={6} title="The Rise of Intentional Dating">
          <p>
            Quality over quantity is no longer marketing copy. It is a structural shift in how Black singles are using dating platforms. Long-form profiles, video introductions, themed lounges, and slow-paced communication are replacing the rapid-swipe, low-context experience that dominated the prior decade.
          </p>
          <p>
            This shift has economic implications too. Members are willing to pay for platforms that genuinely help them find someone — and to leave platforms that optimize for engagement over connection. The dating apps that thrive in the next five years will be the ones that get this trade-off right.
          </p>
        </ReportSection>

        <ReportSection n={7} title="Looking Ahead — Predictions For 2027">
          <p>
            Based on the patterns we are seeing, here is what we expect to define the next chapter:
          </p>
          <ul className="list-disc list-inside ml-2 space-y-2">
            <li><strong className="text-slate-900">Verification becomes default.</strong> Platforms without it will struggle to retain serious members.</li>
            <li><strong className="text-slate-900">Plural family-building goes mainstream-adjacent.</strong> The conversation is moving from fringe to widely discussed.</li>
            <li><strong className="text-slate-900">Community-led dating spaces grow.</strong> The era of the generic mega-app is ending; identity- and intention-specific platforms will take share.</li>
            <li><strong className="text-slate-900">AI as a tool, not a partner.</strong> AI-assisted matching becomes useful infrastructure; AI as a stand-in for human connection loses its appeal.</li>
            <li><strong className="text-slate-900">Marriage rates among intentional cohorts rise.</strong> The Black singles seeking long-term partnership are increasingly finding it — when they choose platforms designed for that goal.</li>
          </ul>
        </ReportSection>

        <AboutThisReport published={PUBLISHED_HUMAN} />

        {/* Internal links */}
        <section className="mt-12">
          <h2 className="font-playfair text-2xl font-bold text-slate-900 mb-4">
            Read Next
          </h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <a href="/black-love-dating-app" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">Black Love Dating App</p>
              <p className="text-sm text-slate-600 mt-1">Centering culture and connection.</p>
            </a>
            <a href="/dating-app-for-intentional-relationships" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">Dating App For Intentional Relationships</p>
              <p className="text-sm text-slate-600 mt-1">Why slow dating works better.</p>
            </a>
            <a href="/verified-dating-app" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">Verified Dating App</p>
              <p className="text-sm text-slate-600 mt-1">How verification creates safer connections.</p>
            </a>
            <a href="/african-diaspora-dating" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">African Diaspora Dating</p>
              <p className="text-sm text-slate-600 mt-1">Connections across the global Black community.</p>
            </a>
            <a href="/guides" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition sm:col-span-2">
              <p className="font-semibold text-slate-900">All UnityLove Guides</p>
              <p className="text-sm text-slate-600 mt-1">Browse the full library.</p>
            </a>
          </div>
        </section>
      </article>

      <BottomCTA />
    </div>
  );
}
